/**
 * abc-personalize.js
 * Product shelf personalization for [data-abc-product-shelf] sections.
 *
 * What it does:
 *   1. On visit 2+ with behavioral.lastProducts in localStorage → fetch Shopify ML
 *      recommendations seeded from lastProducts[0], inject a "For You" tab as the
 *      first (active) tab in each abc-product-shelf section on the page.
 *   2. On visit 2+ (without product history) → activate the tab whose
 *      data-priority-stage matches the visitor's current visit stage.
 *   3. Falls back to as-configured (first tab active) when no personalization applies.
 *
 * Data presence in localStorage implies prior consent — data is only written when
 * consent is granted. Revoking consent clears localStorage (abc-behavioral.js),
 * so absence of data is the correct gate.
 *
 * Fires:
 *   abc:personalizeReady — when evaluation is complete (even if nothing changed)
 *   abc:shelfInjected    — { sectionEl, sliderEl } when a "For You" tab is injected
 */
(function () {
  'use strict';

  var FOR_YOU_LABEL   = 'For You';
  var RECS_LIMIT      = 10; // fetch more so slider has room to scroll
  var MIN_RECS        = 2;  // minimum products before we show the tab
  var ATTR_SESSION_KEY = 'abc_attr'; // sessionStorage key for cross-page attribution
  var ATTR_TTL_MS      = 30 * 60 * 1000; // 30 min attribution window

  // ── Analytics helpers ──────────────────────────────────────────────────────
  // analytics.publish() is Shopify's Custom Pixel API — available in browser context,
  // forwarded to GA4 via the abc-personalization Custom Pixel in Shopify Admin.
  function publishEvent(name, data) {
    var pub = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish;
    if (typeof pub === 'function') {
      pub(name, data);
    } else {
      setTimeout(function () {
        var pub2 = window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish;
        if (typeof pub2 === 'function') pub2(name, data);
      }, 500);
    }
  }

  
  // Build the context object attached to every personalization event.
  // Keeps all events consistently keyed so GA4 custom dimensions can slice by each field.
  function buildContext(seedType, stage, seedProductId) {
    var customer = window.__abc && window.__abc.customer;
    return {
      item_list_id:      'abc_for_you',
      item_list_name:    'For You',
      seed_type:         seedType,       // 'order_history' | 'behavioral'
      visit_stage:       stage,          // 'Discovery' | 'Consideration' | 'Conversion' | 'Subscription'
      customer_tier:     customer ? (customer.tier || null) : null,
      customer_is_club:  customer ? !!customer.isClub : false,
      seed_product_id:   seedProductId,
    };
  }

  // Attach click tracking to the injected For You panel via event delegation.
  // Also writes sessionStorage attribution so the PDP add-to-cart can link back.
  function attachClickTracking(panel, context) {
    panel.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/products/"]');
      if (!link) return;

      var parts     = link.href.split('/products/');
      var handle    = parts[1] ? parts[1].split('?')[0] : null;
      var li        = link.closest('li.multicolumn-list__item');
      var allItems  = panel.querySelectorAll('li.multicolumn-list__item');
      var pos       = li ? Array.from(allItems).indexOf(li) : 0;
      var pid       = li ? (li.dataset.productId || null) : null;
      var titleEl   = li ? li.querySelector('.card__heading a, .card__heading') : null;
      var title     = titleEl ? titleEl.textContent.trim() : handle;

      var payload = Object.assign({}, context, {
        recommended_product_handle: handle,
        recommended_product_title:  title,
        recommended_product_id:     pid,
        position:                   pos,
      });

      // Write cross-page attribution — PDP add-to-cart reads this from sessionStorage
      try {
        sessionStorage.setItem(ATTR_SESSION_KEY, JSON.stringify(
          Object.assign({}, payload, { ts: Date.now() })
        ));
      } catch (e) {}

      publishEvent('abc_recommendation_click', payload);
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  // Stage is the authoritative output of abc-behavioral.js (window.__abc.behavioral.visitStage).
  // Read from there when available. Only recalculate if behavioral hasn't initialized.
  function getStage(visitCount) {
    var stage = window.__abc && window.__abc.behavioral && window.__abc.behavioral.visitStage;
    if (stage) return stage;
    if (!visitCount || visitCount <= 1) return 'Discovery';
    if (visitCount <= 3) return 'Consideration';
    if (visitCount <= 6) return 'Conversion';
    return 'Subscription';
  }

  function getBehavioral() {
    var b = window.__abc && window.__abc.behavioral;
    if (b) return b;
    try {
      var v = JSON.parse(localStorage.getItem('abc_v') || 'null') || {};
      var p = JSON.parse(localStorage.getItem('abc_products') || 'null');
      return {
        visitCount:   v.count   || 0,
        lastProducts: p && p.ids ? p.ids : [],
        lastCategory: null,
      };
    } catch (e) {
      return { visitCount: 0, lastProducts: [] };
    }
  }

  // ── Tab switcher ───────────────────────────────────────────────────────────
  function activateTab(section, button) {
    var blockId = button.getAttribute('block-id');
    if (!blockId) return;

    // Deactivate all — include tabindex so Dawn's featured-collections element
    // doesn't re-activate a tab based on tabindex="0" being present
    section.querySelectorAll('.featured_collections_collection_title').forEach(function (btn) {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('tabindex', '-1');
    });
    section.querySelectorAll('.featured_collections_collection').forEach(function (panel) {
      panel.classList.remove('active');
    });

    // Activate target
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');
    button.setAttribute('tabindex', '0');
    var panel = section.querySelector('#tabpanel-' + blockId);
    if (panel) panel.classList.add('active');
  }

  // ── Inject "For You" tab ───────────────────────────────────────────────────
  function injectForYouTab(section, itemsHtml, count, context) {
    var tabList   = section.querySelector('.featured_collections_collection_titles');
    var nav       = section.querySelector('.featured_collections_nav');
    var container = nav && nav.parentNode;
    if (!tabList || !container) return;

    var forYouId = 'abc-for-you-' + section.id;

    // Don't inject twice
    if (section.querySelector('[block-id="' + forYouId + '"]')) return;

    // Build tab button
    var btn = document.createElement('button');
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', 'false');
    btn.setAttribute('aria-controls', 'tabpanel-' + forYouId);
    btn.setAttribute('id', 'tab-' + forYouId);
    btn.setAttribute('class', 'featured_collections_collection_title');
    btn.setAttribute('block-id', forYouId);
    btn.setAttribute('tabindex', '0');
    btn.textContent = section.dataset.forYouLabel || FOR_YOU_LABEL;

    // Insert as first tab
    tabList.insertBefore(btn, tabList.firstChild);

    // Inherit column count from existing slider
    var colClass = 'grid--5-col-desktop';
    var existingUl = section.querySelector('.multicolumn-list');
    if (existingUl) {
      var colMatch = existingUl.className.match(/grid--(\d)-col-desktop/);
      if (colMatch) colClass = 'grid--' + colMatch[1] + '-col-desktop';
    }

    // Build panel
    var panel = document.createElement('div');
    panel.setAttribute('class', 'featured_collections_collection');
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('id', 'tabpanel-' + forYouId);
    panel.setAttribute('aria-labelledby', 'tab-' + forYouId);
    panel.setAttribute('block-id', forYouId);
    panel.innerHTML =
      '<slider-component class="multicolumn_slider slider-component-abc slider-mobile-gutter" role="region" aria-roledescription="carousel">' +
        '<ul class="multicolumn-list contains-content-container grid grid--1-col grid--2-col-tablet-down ' + colClass + ' slider slider--everywhere grid--peek" id="Slider-' + forYouId + '" role="none">' +
          itemsHtml +
        '</ul>' +
        '<div class="slideshow_nav_btn_wrapper small-hide">' +
          '<button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Previous slide" aria-controls="Slider-' + forYouId + '">' +
            '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.707 0L5 4.293 1.354.646a.5.5 0 00-.707.707l4 4a.5.5 0 00.707 0l4-4a.5.5 0 000-.707z" fill="currentColor"/></svg>' +
          '</button>' +
          '<button type="button" class="slider-button slider-button--next" name="next" aria-label="Next slide" aria-controls="Slider-' + forYouId + '">' +
            '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.707 0L5 4.293 1.354.646a.5.5 0 00-.707.707l4 4a.5.5 0 00.707 0l4-4a.5.5 0 000-.707z" fill="currentColor"/></svg>' +
          '</button>' +
        '</div>' +
      '</slider-component>';

    // Insert before the first existing panel
    var firstPanel = section.querySelector('.featured_collections_collection');
    if (firstPanel) {
      container.insertBefore(panel, firstPanel);
    } else {
      container.appendChild(panel);
    }

    // Take over click handling for ALL tabs in this section.
    // The featured-collections custom element bound its own handlers before we injected,
    // so it doesn't know about the For You panel. Replace with our activateTab so every
    // button correctly shows/hides all panels.
    var liveBtn = btn;
    section.querySelectorAll('.featured_collections_collection_title').forEach(function (tabBtn) {
      var clone = tabBtn.cloneNode(true); // strips old event listeners
      tabBtn.parentNode.replaceChild(clone, tabBtn);
      if (tabBtn === btn) liveBtn = clone;
      clone.addEventListener('click', function () { activateTab(section, clone); });
    });

    // Activate the For You tab
    activateTab(section, liveBtn);

    // Track impression and wire click tracking
    if (context) {
      var impressionItems = [];
      panel.querySelectorAll('li.multicolumn-list__item').forEach(function (li, i) {
        var titleEl = li.querySelector('.card__heading a, .card__heading');
        impressionItems.push({
          item_id:        li.dataset.productId || null,
          item_name:      titleEl ? titleEl.textContent.trim() : null,
          index:          i,
          item_list_id:   context.item_list_id,
          item_list_name: context.item_list_name,
        });
      });
      publishEvent('abc_for_you_impression', Object.assign({}, context, { product_count: count, items: impressionItems }));
      attachClickTracking(panel, context);
    }

    // Inject promoted product pre-rendered in the section, if any
    var promotedTpl = section.querySelector('[data-abc-promoted-card]');
    if (promotedTpl && promotedTpl.content) {
      var listEl = panel.querySelector('.multicolumn-list');
      if (listEl) {
        var position = parseInt(promotedTpl.dataset.position || '1', 10) - 1;
        var clonedLi = promotedTpl.content.querySelector('li').cloneNode(true);
        var allItems = listEl.querySelectorAll('li');
        var promotedId = String(clonedLi.dataset.productId || '');
        var isDuplicate = promotedId && Array.from(allItems).some(function (li) {
          return String(li.dataset.productId || '') === promotedId;
        });
        if (!isDuplicate) {
          var ref = allItems[position] || null;
          if (ref) {
            listEl.insertBefore(clonedLi, ref);
          } else {
            listEl.appendChild(clonedLi);
          }
        }
      }
    }

    // Fix slide IDs and call resetPages so SliderComponent can track scroll position
    // and enable/disable prev/next buttons correctly.
    var sliderEl = panel.querySelector('slider-component');
    panel.querySelectorAll('li.multicolumn-list__item').forEach(function (li, i) {
      li.setAttribute('id', 'Slide-' + forYouId + '-' + (i + 1));
    });
    if (sliderEl && typeof sliderEl.resetPages === 'function') {
      sliderEl.resetPages();
    }

    // Nav arrows stay hidden (section CSS default) until the section's inline
    // slider script measures the image and sets a correct vertical position —
    // revealing them here first would flash them at the wrong (default) offset.

    // Notify the section's inline slider script so it can position nav buttons
    document.dispatchEvent(new CustomEvent('abc:shelfInjected', {
      detail: { sectionEl: section, sliderEl: sliderEl }
    }));
  }

  // ── Stage-based tab prioritization ────────────────────────────────────────
  function applyStageTab(section, stage) {
    var buttons = section.querySelectorAll('.featured_collections_collection_title[data-priority-stage]');
    var match = null;
    buttons.forEach(function (btn) {
      if (btn.getAttribute('data-priority-stage') === stage) match = btn;
    });
    if (!match) return false;
    activateTab(section, match);
    return true;
  }

  // ── Fetch + render recommendations ────────────────────────────────────────
  // Uses the Dawn related-products pattern: fetch to the recommendations endpoint
  // with section_id= so Shopify renders abc-shelf-recommendations.liquid server-side.
  // Returns raw HTML (not JSON), unlike /?sections=.
  function fetchAndRenderRecommendations(seedProductId, callback) {
    var url = '/recommendations/products?product_id=' + seedProductId +
      '&limit=' + RECS_LIMIT + '&intent=related&section_id=abc-shelf-recommendations';

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (!html) return callback(null, '', 0);
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var items = tmp.querySelectorAll('li.multicolumn-list__item');
        var count = items.length;
        // SliderComponent finds slides via [id^="Slide-"] — add temp IDs now,
        // real IDs are set in injectForYouTab after insertion.
        items.forEach(function (li, i) {
          li.setAttribute('id', 'Slide-__for-you-tmp__-' + i);
        });
        var itemsHtml = '';
        items.forEach(function (li) { itemsHtml += li.outerHTML; });
        callback(null, itemsHtml, count);
      })
      .catch(function (err) {
        callback(err, '', 0);
      });
  }

  // ── Main evaluation ────────────────────────────────────────────────────────
  function evaluate() {
    var shelves = document.querySelectorAll('[data-abc-product-shelf]');
    if (!shelves.length) {
      document.dispatchEvent(new CustomEvent('abc:personalizeReady'));
      return;
    }

    var behavioral   = getBehavioral();
    var visitCount   = behavioral.visitCount || 0;
    var stage        = getStage(visitCount);
    var lastProducts = behavioral.lastProducts || [];

    // Seed priority: order-derived (logged-in, server-side) > behavioral (localStorage)
    var topProducts = window.__abc && window.__abc.customer && window.__abc.customer.topProducts;
    var seedProduct = (topProducts && topProducts[0] && topProducts[0].pid)
      ? String(topProducts[0].pid)
      : (lastProducts[0] || null);

    // For each shelf on the page, apply personalization independently
    var pending = shelves.length;
    function done() {
      pending--;
      if (pending <= 0) {
        document.dispatchEvent(new CustomEvent('abc:personalizeReady'));
      }
    }

    shelves.forEach(function (section) {
      // Logged-in customers with order history skip the visit count gate —
      // their top products are computed server-side, no behavioral history needed.
      // Anonymous visitors still require visit 2+ before personalization kicks in.
      var hasOrderSeed = topProducts && topProducts.length > 0;
      if (!hasOrderSeed && visitCount <= 1) {
        done();
        return;
      }

      // Seed available → fetch ML recs and inject For You tab
      if (seedProduct) {
        var seedType = hasOrderSeed ? 'order_history' : 'behavioral';
        var ctx = buildContext(seedType, stage, seedProduct);
        fetchAndRenderRecommendations(seedProduct, function (err, itemsHtml, count) {
          if (!err && count >= MIN_RECS && itemsHtml) {
            injectForYouTab(section, itemsHtml, count, ctx);
          } else {
            // Fetch failed or too few results — fall back to stage tab
            applyStageTab(section, stage);
          }
          done();
        });
        return;
      }

      // Visit 2+ but no seed product → stage tab only
      applyStageTab(section, stage);
      done();
    });
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  function boot() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', evaluate);
    } else {
      evaluate();
    }
  }

  if (window.__abc && window.__abc.behavioral !== undefined) {
    boot();
  } else {
    document.addEventListener('abc:behavioralReady', boot);
    document.addEventListener('abc:consentReady', function () {
      if (!window.__abc || window.__abc.behavioral === null) {
        boot();
      }
    });
  }

})();
